import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { signUpUser, clearError } from '../store/authSlice'

export default function SignupForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formErrors, setFormErrors] = useState({})

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error } = useSelector((state) => state.auth)

  const validateForm = () => {
    const errors = {}
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required'
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid'
    }

    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    dispatch(clearError())

    if (!validateForm()) {
      return
    }

    const result = await dispatch(signUpUser({
      fullName: formData.fullName,
      email: formData.email,
      password: formData.password
    }))

    // Check if signup was successful and navigate immediately
    if (signUpUser.fulfilled.match(result)) {
      // Navigate immediately before any auth state changes can trigger redirects
      navigate('/signup/confirm', { state: { email: formData.email } })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Main Card */}
        <div className="apple-card animate-slide-in px-5 py-5">
          {/* Header */}
          <div className="text-center mb-2">
            <div className="w-50 h-auto flex items-center justify-center mx-auto">
              <img
                src="/taskrdo-logo.png"
                alt="taskrdo"
                className="h-16"
              />
            </div>
            <h1 className="apple-text-title">Create account</h1>
            <p className="apple-text-body">Join us to Get Sh·t Done</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name Field */}
            <div className="space-y-2">
              <label htmlFor="fullName" className="block text-sm font-medium text-text-primary">
                Full Name
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                autoComplete="name"
                required
                className={`apple-input ${
                  formErrors.fullName ? 'apple-input-error' : ''
                }`}
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleInputChange}
              />
              {formErrors.fullName && (
                <p className="text-xs text-status-danger mt-1">{formErrors.fullName}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-text-primary">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`apple-input ${
                  formErrors.email ? 'apple-input-error' : ''
                }`}
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleInputChange}
              />
              {formErrors.email && (
                <p className="text-xs text-status-danger mt-1">{formErrors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-text-primary">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`apple-input pr-12 ${
                    formErrors.password ? 'apple-input-error' : ''
                  }`}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p className="text-xs text-status-danger mt-1">{formErrors.password}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`apple-input pr-12 ${
                    formErrors.confirmPassword ? 'apple-input-error' : ''
                  }`}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-text-muted hover:text-text-secondary transition-colors" />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <p className="text-xs text-status-danger mt-1">{formErrors.confirmPassword}</p>
              )}
            </div>

            {/* Terms Notice */}
            <div className="bg-surface-secondary rounded-lg p-4">
              <p className="text-xs text-text-secondary leading-relaxed">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="text-accent-foreground hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-accent-foreground hover:underline">
                  Privacy Policy
                </Link>.
                We'll keep your information safe and never share it with third parties.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-accent-danger border border-status-danger rounded-lg p-3">
                <p className="text-sm text-status-danger">{error}</p>
              </div>
            )}

            {/* Create Account Button */}
            <button
              type="submit"
              disabled={loading}
              className="apple-button-primary w-full relative focus-apple disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>
        </div>

        {/* Sign In Link */}
        <div className="text-center mt-6">
          <p className="apple-text-body">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-accent-foreground hover:text-status-info font-medium transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
